/**
 * PAYWALL (SEPA catch-up) backend error mappings.
 *
 * The `/v2/paywall/*` endpoints return an English `message` (no error_code), so
 * we map those known messages to user-facing localized wording and always fall
 * back to a generic localized message — the frontend never shows the raw
 * backend string.
 */

/** Map: backend `message` -> localized messages. */
export const PAYWALL_ERROR_MESSAGES_BY_MESSAGE = {
  'No billing customer for this company.': {
    fr: "Aucun compte de facturation n'est associé à cette entreprise. Veuillez contacter le support.",
    en: 'No billing account is associated with this company. Please contact support.',
  },
  'No outstanding debt to settle.': {
    fr: 'Aucun montant à régulariser.',
    en: 'No outstanding amount to settle.',
  },
  'No catch-up invoice to retry.': {
    fr: 'Aucun paiement de régularisation en cours à relancer.',
    en: 'No pending catch-up payment to retry.',
  },
  'Could not create the catch-up invoice.': {
    fr: 'La préparation du paiement a échoué. Veuillez réessayer ou contacter le support.',
    en: 'Could not prepare the payment. Please try again or contact support.',
  },
  'Could not retry the catch-up invoice.': {
    fr: 'La relance du paiement a échoué. Veuillez réessayer ou contacter le support.',
    en: 'Could not retry the payment. Please try again or contact support.',
  },
  'Could not compute paywall status.': {
    fr: 'Impossible de vérifier le statut de votre compte. Veuillez réessayer.',
    en: 'Could not check your account status. Please try again.',
  },
};

const PAYWALL_GENERIC_MESSAGE = {
  fr: 'Une erreur est survenue lors de la régularisation. Veuillez réessayer ou contacter le support.',
  en: 'Something went wrong during the payment. Please try again or contact support.',
};

export function isPaywallApi(api) {
  return typeof api === 'string' && api.startsWith('paywall/');
}

/**
 * Resolve a localized message for a paywall backend error payload. Always
 * returns a message (mapped or generic) so no raw backend string leaks through.
 */
export function resolvePaywallErrorMessage({error, locale = 'fr'} = {}) {
  const normalizedLocale = locale === 'en' ? 'en' : 'fr';
  const mapped =
    error?.message && PAYWALL_ERROR_MESSAGES_BY_MESSAGE[error.message];
  return (mapped || PAYWALL_GENERIC_MESSAGE)[normalizedLocale];
}
